import { defineComponent, reactive, SetupContext, ref, watch } from '@vue/composition-api';
import SystemConst from '@/shared/system-const';
import HttpStatus from 'http-status-codes';
import { Category } from '@/shared/type';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';          // 共通メッセージダイアログコンポーネント
import vueRouter from '@/router';
import MessageConst from '@/shared/message-const';

/**
 * リアクティブな変数の型定義
 */
export type reactiveData = {
    windowSize: { x: number; y: number };                       // Windowサイズ
    categoryItem: Category[];                                   // カテゴリ
};

/**
 * カテゴリコンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                        // Windowサイズ
            categoryItem: []                                                   // カテゴリ
        });
        const selectCategoryHeight = SystemConst.SELECT_CATEGORY_SIZE.HEIGHT;  // カテゴリー選択高さ調整用変数
        const commonRef = ref<any>(null);                                      // 共通ダイアログ
        const activeNode = ref<any[]>([]);                                     // 組織ツリーでactiveなノードを管理する変数
        const router = vueRouter;                                              // 画面遷移用

        /**
         * windowサイズ設定（本関数が使用されるのは親コンポーネントのためvueで使用しないが、エラーになるためreturnに記述）
         * @param windowSize Windowサイズ
         */
        function resize(windowSize: any) {
            state.windowSize = windowSize;
        }

        /**
         * 初期表示
         */
        created();
        async function created() {
            // ストアに保存されているカテゴリ情報とアクティブ状態のノードを復元する
            state.categoryItem = ctx.root.$store.state.category.officialDocumentCategory;
            activeNode.value[0] = ctx.root.$store.state.officialDocument.activeNode[0];
        }

        /**
         * カテゴリの取得
         *
         * @param boolean isOfficialDocumentDetail 公文書詳細フラグ（true：公文書詳細画面から呼び出している、false：公文書詳細画面以外から呼び出している)
         **/
        async function getCategory(isOfficialDocumentDetail = false) {
            // カテゴリ情報取得APIを呼び出す
            //   パラメータ: isSystemManageMode
            const response = await ctx.root.$store.dispatch('category/getCategory');

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 取得したデータをstate.categoryItemに設定する
            state.categoryItem = response.data.categories;

            // カテゴリ情報をストアに保存する
            ctx.root.$store.commit('category/setOfficialDocumentCategory', state.categoryItem);

            if (ctx.root.$store.state.officialDocument.activeNode.length === 0) {
                // ストアにアクティブなノードが保存されていない場合
                for (let i = 0; i < state.categoryItem.length; i++) {
                    if (state.categoryItem[i].id === ctx.root.$store.state.authenticationUser.organizationId) {
                        // 取得したカテゴリにログインユーザーの所属組織が含まれる場合
                        // ログインユーザーの所属組織をアクティブにする
                        activeNode.value = [ctx.root.$store.state.authenticationUser.organizationId];

                        if (!isOfficialDocumentDetail) {
                            // 書き込み権限があるか
                            if (ctx.root.$store.state.authenticationUser.role.isOfficialDocument) {
                                // 公文書一覧（管理）画面へ遷移
                                router.push('/official-documents/admin/list');
                            }
                        }

                        return;
                    }
                }

                // カテゴリにログインユーザーの所属組織が含まれない場合
                // カテゴリの一番上の組織をアクティブにする
                activeNode.value = [state.categoryItem[0].id];
            } else {
                // ストアにアクティブなノードが保存されている場合
                if (state.categoryItem.map(item => item['id']).includes(ctx.root.$store.state.officialDocument.activeNode[0])) {
                    // ストアに保存しているノードが取得したカテゴリ情報と一致する場合
                    // ストアに保存しているノードをアクティブにする
                    activeNode.value = ctx.root.$store.state.officialDocument.activeNode;
                } else {
                    // ストアに保存しているノードが取得したカテゴリ情報と一致しない場合
                    // エラー画面へ遷移
                    ctx.root.$store.commit('setErrorMessage', MessageConst.E_INVALID_CATEGORY);
                    router.push('/error');
                }
            }
        }

        /**
         * 組織ツリーでノードのactive情報が更新された際に発火する
         * 
         * @param node nodeオブジェクト
         */
        watch(activeNode, () => watchActiveNode());
        function watchActiveNode() {
            // 選択されたノードを再度押下し、カテゴリ選択が未選択となった場合
            if (activeNode.value.length === 0) {
                // 直前にアクティブだったノードを再びアクティブにする
                activeNode.value[0] = ctx.root.$store.state.officialDocument.activeNode[0];
            } else {
                // 前回選択されたノードと別のノードを選択された場合
                if (activeNode.value[0] !== ctx.root.$store.state.officialDocument.activeNode[0]) {
                    // ストアにactiveなノードの状態を保存する
                    ctx.root.$store.commit('officialDocument/setActiveNode', activeNode.value);
                }
            }
        }

        /**
         * 親コンポーネントの初期表示時にカテゴリが変更された場合に選択中のカテゴリを変更する
         */
        function onActive() {
            // 親コンポーネントでの変更を反映する
            ctx.root.$set(activeNode.value, 0, ctx.root.$store.state.officialDocument.activeNode[0]);
        }

        return {
            state,
            selectCategoryHeight,
            commonRef,
            activeNode,
            resize,
            getCategory,
            onActive
        };
    }
});
